<div>
  <!-- TOP SECTION -->
  <p class="text-sm font-bold text-navy-900 mb-5">Stage A: Creation</p>

  <!-- Incident Details -->
  <section class="mb-8">
    <!-- Date and Time — first actionable control -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-semibold text-navy-600 tracking-wider"
          >Date and Time of Incident</label
        >
        <app-date-picker
          [value]="incidentDate()"
          placeholder="Select date…"
          [disabled]="topDisabled()"
          [error]="showErrors()"
          class="w-full"
          (valueChange)="incidentDate.set($event)"
        />
        @if (showErrors() && !incidentDate()) {
          <p class="text-2xs text-red-500">This field is required.</p>
        }
      </div>
    </div>

    <!-- Substantive Area, Program, Location -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-semibold text-navy-600 tracking-wider">Division</label>
        <app-select
          [options]="substantiveAreaOptions()"
          [value]="substantiveArea()"
          [loading]="substantiveAreaLoading()"
          placeholder="Select area"
          (changed)="onSubstantiveAreaChange($event)"
          [error]="showErrors()"
          [class.pointer-events-none]="topDisabled()"
          [class.opacity-60]="topDisabled()"
        />
        @if (showErrors() && !substantiveArea()) {
          <p class="text-2xs text-red-500">This field is required.</p>
        }
      </div>
      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-semibold text-navy-600 tracking-wider">Program</label>
        <app-select
          [options]="programOptions()"
          [value]="program()"
          [loading]="programLoading()"
          placeholder="Select program"
          (changed)="onProgramChange($event)"
          [error]="showErrors()"
          [class.pointer-events-none]="topDisabled()"
          [class.opacity-60]="topDisabled()"
        />
        @if (showErrors() && !program()) {
          <p class="text-2xs text-red-500">This field is required.</p>
        }
      </div>
      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-semibold text-navy-600 tracking-wider">Location</label>
        <app-select
          [options]="locationOptions()"
          [value]="location()"
          [loading]="locationLoading()"
          placeholder="Choose a location"
          (changed)="location.set($event)"
          [class.pointer-events-none]="topDisabled()"
          [class.opacity-60]="topDisabled()"
        />
      </div>
    </div>
  </section>

  <!-- Clients Involved -->
  <section class="mb-8">
    <p class="text-sm font-bold text-navy-900 mb-5">Clients Involved</p>

    <div
      class="flex items-center bg-slate-100 rounded-3xl px-1 py-1 gap-1 mb-5 w-fit"
      [class.opacity-60]="topDisabled()"
    >
      @for (opt of partiesTypeOptions; track opt.id) {
        <button
          type="button"
          (click)="!topDisabled() && setPartiesType(opt.id)"
          class="px-5 py-1.5 rounded-full text-xs font-medium transition-all"
          [class.cursor-pointer]="!topDisabled()"
          [class.cursor-default]="topDisabled()"
          [ngClass]="
            partiesType() === opt.id
              ? 'bg-white text-navy-900 shadow-sm'
              : 'text-slate-500 hover:text-navy-700'
          "
        >
          {{ opt.label }}
        </button>
      }
    </div>

    @if (partiesType() !== 'na') {
      <div class="flex flex-col gap-3">
        @for (party of parties(); track party.id) {
          <div class="rounded-2xl border border-surface-400 bg-white">
            <button
              type="button"
              (click)="toggleParty(party.id)"
              class="w-full flex items-center gap-3 px-5 py-4 hover:bg-slate-50 transition-colors cursor-pointer text-left rounded-t-2xl"
            >
              <div
                class="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0"
              >
                <img src="/icons/user-icon.svg" class="w-4 h-4" alt="" />
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-xs font-semibold text-navy-900">
                  {{ party.name }}
                  @if (party.tagline) {
                    <span class="font-normal text-slate-400">· {{ party.tagline }}</span>
                  }
                </p>
                @if (personTypeLabel(party)) {
                  <p class="text-2xs text-slate-400">{{ personTypeLabel(party) }}</p>
                }
              </div>
              @if (!topDisabled()) {
                <button
                  type="button"
                  (click)="$event.stopPropagation(); removeParty(party.id)"
                  class="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer"
                >
                  <img src="/icons/close-icon.svg" class="w-3.5 h-3.5" alt="Remove" />
                </button>
              }
              <img
                src="/icons/chevron-down-icon.svg"
                class="w-4 h-4 shrink-0 transition-transform opacity-40"
                [class.-rotate-180]="expandedPartyId() === party.id"
                alt=""
              />
            </button>

            @if (expandedPartyId() === party.id) {
              <div class="border-t border-surface-400">
                <!-- PI Involvement Type -->
                <div class="flex items-center gap-3 px-4 sm:px-5 py-3 border-b border-surface-400">
                  <label class="text-xs font-semibold text-navy-600 tracking-wider shrink-0 w-36"
                    >Involvement Type</label
                  >
                  <div class="flex flex-col gap-1 flex-1">
                    <app-select
                      [options]="getInvolvementTypeOptions(party.personTypeCode)"
                      [value]="party.piInvolvementTypeId"
                      placeholder="Select type…"
                      [borderless]="false"
                      (changed)="onPartyInvolvementTypeChange(party.id, $event)"
                      [class.pointer-events-none]="topDisabled()"
                      [class.opacity-60]="topDisabled()"
                      [class.ring-2]="
                        (showErrors() || showInvolvementErrors()) && !party.piInvolvementTypeId
                      "
                      [class.ring-red-200]="
                        (showErrors() || showInvolvementErrors()) && !party.piInvolvementTypeId
                      "
                      [class.border-red-400]="
                        (showErrors() || showInvolvementErrors()) && !party.piInvolvementTypeId
                      "
                    />
                    @if ((showErrors() || showInvolvementErrors()) && !party.piInvolvementTypeId) {
                      <p class="text-2xs text-red-500">This field is required.</p>
                    }
                  </div>
                </div>

                @if (
                  party.dataElements.filter(
                    (e) =>
                      !e.piInvolvementTypeId || e.piInvolvementTypeId === party.piInvolvementTypeId
                  ).length > 0
                ) {
                  <!-- Header -->
                  <div
                    class="grid grid-cols-12 items-center gap-x-3 px-4 sm:px-5 py-2.5 bg-slate-50 border-b border-surface-400"
                  >
                    <span class="col-span-1 text-2xs font-semibold text-slate-400"
                      >&#123;&#125;</span
                    >
                    <span
                      class="col-span-5 text-2xs font-semibold text-slate-500 tracking-wider uppercase"
                      >Data Element</span
                    >
                    <span
                      class="col-span-5 text-2xs font-semibold text-slate-500 tracking-wider uppercase"
                      >Value</span
                    >
                    <span class="col-span-1"></span>
                  </div>

                  @for (
                    el of party.dataElements.filter(
                      (e) =>
                        !e.piInvolvementTypeId ||
                        e.piInvolvementTypeId === party.piInvolvementTypeId
                    );
                    track el.order
                  ) {
                    <div
                      class="grid grid-cols-12 items-center gap-x-3 px-4 sm:px-5 py-3 border-b border-slate-50 last:border-0"
                    >
                      <span class="col-span-1 text-2xs text-slate-400">{{ el.order }}.</span>
                      <span class="col-span-5 text-xs text-navy-900 min-w-0 truncate">
                        {{ el.label }}
                        @if (el.required) {
                          <span class="text-red-500 ml-0.5">*</span>
                        }
                      </span>

                      <!-- Value -->
                      <div class="col-span-5">
                        @if (el.dataType === 'Datetime') {
                          <input
                            type="date"
                            class="w-auto border-0 bg-transparent p-0 text-xs text-navy-900 focus:ring-0 focus:outline-none cursor-pointer"
                            [value]="el.value"
                            [disabled]="topDisabled()"
                            (click)="$any($event.target).showPicker?.()"
                            (change)="onPartyElementChange(el, $any($event.target).value)"
                          />
                        } @else if (el.dataType === 'Note') {
                          <textarea
                            rows="2"
                            placeholder="—"
                            class="w-full border-0 bg-transparent p-0 text-xs focus:ring-0 focus:outline-none resize-none"
                            [ngClass]="el.value ? 'text-navy-900' : 'text-slate-400'"
                            [disabled]="topDisabled()"
                            (input)="onPartyElementChange(el, $any($event.target).value)"
                            >{{ el.value }}</textarea
                          >
                        } @else if (el.dataType === 'Integer' || el.dataType === 'Float') {
                          <input
                            type="number"
                            [step]="el.dataType === 'Float' ? 'any' : '1'"
                            placeholder="—"
                            class="border-0 bg-transparent p-0 text-xs focus:ring-0 focus:outline-none"
                            [ngClass]="el.value ? 'text-navy-900' : 'text-slate-400'"
                            [value]="el.value"
                            [disabled]="topDisabled()"
                            (input)="onPartyElementChange(el, $any($event.target).value)"
                          />
                        } @else if (el.dataType === 'Yes/No' || el.dataType === 'List Item') {
                          <app-select
                            [borderless]="true"
                            [options]="el.options"
                            [keepOrder]="true"
                            [value]="el.value"
                            placeholder="Select"
                            (changed)="onPartyElementChange(el, $event)"
                            [class.pointer-events-none]="topDisabled()"
                            [class.opacity-60]="topDisabled()"
                          />
                        } @else {
                          <input
                            type="text"
                            placeholder="—"
                            class="border-0 bg-transparent p-0 text-xs focus:ring-0 focus:outline-none"
                            [ngClass]="el.value ? 'text-navy-900' : 'text-slate-400'"
                            [value]="el.value"
                            [disabled]="topDisabled()"
                            (input)="onPartyElementChange(el, $any($event.target).value)"
                          />
                        }
                      </div>

                      <!-- Status -->
                      <div class="col-span-1 flex justify-end">
                        @if (el.completed) {
                          <img src="/icons/check-circle-green-icon.svg" class="w-5 h-5" alt="" />
                        } @else if ((showErrors() || showDataElementErrors()) && el.required) {
                          <div
                            class="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center shrink-0"
                          >
                            <img
                              src="/icons/close-icon.svg"
                              class="w-2.5 h-2.5 brightness-0 invert"
                              alt=""
                            />
                          </div>
                        } @else {
                          <div
                            class="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center shrink-0"
                          >
                            <img
                              src="/icons/check-white-icon.svg"
                              class="w-2.5 h-2.5 opacity-50"
                              alt=""
                            />
                          </div>
                        }
                      </div>
                    </div>
                  }
                }
              </div>
            }
          </div>
        }
        @if (canAddParty() && canEditTop() && currentStage() !== 'closed') {
          <button
            type="button"
            (click)="showAddClientModal.set(true)"
            class="text-xs font-semibold text-accent-500 hover:text-accent-600 transition-colors cursor-pointer text-left"
          >
            + Add client
          </button>
        }
      </div>
      @if (showErrors() && partiesCountError() && currentStage() !== 'closed') {
        <p class="text-2xs text-red-500 mt-2">{{ partiesCountError() }}</p>
      }
    }
  </section>

  @if (showAddClientModal()) {
    <app-add-party-modal
      personTypeCode="CL"
      (personSelected)="onPersonSelected($event)"
      (closed)="showAddClientModal.set(false)"
    />
  }
  @if (showAddOtherModal()) {
    <app-add-party-modal
      excludePersonTypeCode="CL"
      (personSelected)="onOtherSelected($event)"
      (closed)="showAddOtherModal.set(false)"
    />
  }

  <!-- Others Involved -->
  <section class="mb-8">
    <p class="text-sm font-bold text-navy-900 mb-5">Others Involved</p>

    <div class="flex flex-col gap-3">
      @for (other of others(); track other.id) {
        <div class="rounded-2xl border border-surface-400 bg-white">
          <button
            type="button"
            (click)="toggleOther(other.id)"
            class="w-full flex items-center gap-3 px-5 py-4 hover:bg-slate-50 transition-colors cursor-pointer text-left rounded-t-2xl"
          >
            <div
              class="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0"
            >
              <img src="/icons/user-icon.svg" class="w-4 h-4" alt="" />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-xs font-semibold text-navy-900">
                {{ other.name }}
                @if (other.tagline) {
                  <span class="font-normal text-slate-400">· {{ other.tagline }}</span>
                }
              </p>
              @if (personTypeLabel(other)) {
                <p class="text-2xs text-slate-400">{{ personTypeLabel(other) }}</p>
              }
            </div>
            @if (!topDisabled()) {
              <button
                type="button"
                (click)="$event.stopPropagation(); removeOther(other.id)"
                class="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer"
              >
                <img src="/icons/close-icon.svg" class="w-3.5 h-3.5" alt="Remove" />
              </button>
            }
            <img
              src="/icons/chevron-down-icon.svg"
              class="w-4 h-4 shrink-0 transition-transform opacity-40"
              [class.-rotate-180]="expandedOtherId() === other.id"
              alt=""
            />
          </button>

          @if (expandedOtherId() === other.id) {
            <div class="border-t border-surface-400">
              <!-- PI Involvement Type -->
              <div class="flex items-center gap-3 px-4 sm:px-5 py-3 border-b border-surface-400">
                <label class="text-xs font-semibold text-navy-600 tracking-wider shrink-0 w-36"
                  >Involvement Type</label
                >
                <div class="flex flex-col gap-1 flex-1">
                  <app-select
                    [options]="getInvolvementTypeOptions(other.personTypeCode)"
                    [value]="other.piInvolvementTypeId"
                    placeholder="Select type…"
                    [borderless]="false"
                    (changed)="onOtherInvolvementTypeChange(other.id, $event)"
                    [class.pointer-events-none]="topDisabled()"
                    [class.opacity-60]="topDisabled()"
                    [class.ring-2]="
                      (showErrors() || showInvolvementErrors()) && !other.piInvolvementTypeId
                    "
                    [class.ring-red-200]="
                      (showErrors() || showInvolvementErrors()) && !other.piInvolvementTypeId
                    "
                    [class.border-red-400]="
                      (showErrors() || showInvolvementErrors()) && !other.piInvolvementTypeId
                    "
                  />
                  @if ((showErrors() || showInvolvementErrors()) && !other.piInvolvementTypeId) {
                    <p class="text-2xs text-red-500">This field is required.</p>
                  }
                </div>
              </div>

              @if (
                other.dataElements.filter(
                  (e) =>
                    !e.piInvolvementTypeId || e.piInvolvementTypeId === other.piInvolvementTypeId
                ).length > 0
              ) {
                <!-- Header -->
                <div
                  class="grid grid-cols-12 items-center gap-x-3 px-4 sm:px-5 py-2.5 bg-slate-50 border-b border-surface-400"
                >
                  <span class="col-span-1 text-2xs font-semibold text-slate-400">&#123;&#125;</span>
                  <span
                    class="col-span-5 text-2xs font-semibold text-slate-500 tracking-wider uppercase"
                    >Data Element</span
                  >
                  <span
                    class="col-span-5 text-2xs font-semibold text-slate-500 tracking-wider uppercase"
                    >Value</span
                  >
                  <span class="col-span-1"></span>
                </div>

                @for (
                  el of other.dataElements.filter(
                    (e) =>
                      !e.piInvolvementTypeId || e.piInvolvementTypeId === other.piInvolvementTypeId
                  );
                  track el.order
                ) {
                  <div
                    class="grid grid-cols-12 items-center gap-x-3 px-4 sm:px-5 py-3 border-b border-slate-50 last:border-0"
                  >
                    <span class="col-span-1 text-2xs text-slate-400">{{ el.order }}.</span>
                    <span class="col-span-5 text-xs text-navy-900 min-w-0 truncate">{{
                      el.label
                    }}</span>

                    <div class="col-span-5">
                      @if (el.dataType === 'Datetime') {
                        <input
                          type="date"
                          class="w-auto border-0 bg-transparent p-0 text-xs text-navy-900 focus:ring-0 focus:outline-none cursor-pointer"
                          [value]="el.value"
                          [disabled]="topDisabled()"
                          (click)="$any($event.target).showPicker?.()"
                          (change)="onOtherElementChange(el, $any($event.target).value)"
                        />
                      } @else if (el.dataType === 'Note') {
                        <textarea
                          rows="2"
                          placeholder="—"
                          class="w-full border-0 bg-transparent p-0 text-xs focus:ring-0 focus:outline-none resize-none"
                          [ngClass]="el.value ? 'text-navy-900' : 'text-slate-400'"
                          [disabled]="topDisabled()"
                          (input)="onOtherElementChange(el, $any($event.target).value)"
                          >{{ el.value }}</textarea
                        >
                      } @else if (el.dataType === 'Integer' || el.dataType === 'Float') {
                        <input
                          type="number"
                          [step]="el.dataType === 'Float' ? 'any' : '1'"
                          placeholder="—"
                          class="border-0 bg-transparent p-0 text-xs focus:ring-0 focus:outline-none"
                          [ngClass]="el.value ? 'text-navy-900' : 'text-slate-400'"
                          [value]="el.value"
                          [disabled]="topDisabled()"
                          (input)="onOtherElementChange(el, $any($event.target).value)"
                        />
                      } @else if (el.dataType === 'Yes/No' || el.dataType === 'List Item') {
                        <app-select
                          [borderless]="true"
                          [options]="el.options"
                          [keepOrder]="true"
                          [value]="el.value"
                          placeholder="Select"
                          (changed)="onOtherElementChange(el, $event)"
                          [class.pointer-events-none]="topDisabled()"
                          [class.opacity-60]="topDisabled()"
                        />
                      } @else {
                        <input
                          type="text"
                          placeholder="—"
                          class="border-0 bg-transparent p-0 text-xs focus:ring-0 focus:outline-none"
                          [ngClass]="el.value ? 'text-navy-900' : 'text-slate-400'"
                          [value]="el.value"
                          [disabled]="topDisabled()"
                          (input)="onOtherElementChange(el, $any($event.target).value)"
                        />
                      }
                    </div>

                    <div class="col-span-1 flex justify-end">
                      @if (el.completed) {
                        <img src="/icons/check-circle-green-icon.svg" class="w-5 h-5" alt="" />
                      } @else if (showErrors()) {
                        <div
                          class="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center shrink-0"
                        >
                          <img
                            src="/icons/close-icon.svg"
                            class="w-2.5 h-2.5 brightness-0 invert"
                            alt=""
                          />
                        </div>
                      } @else {
                        <div
                          class="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center shrink-0"
                        >
                          <img
                            src="/icons/check-white-icon.svg"
                            class="w-2.5 h-2.5 opacity-50"
                            alt=""
                          />
                        </div>
                      }
                    </div>
                  </div>
                }
              }
            </div>
          }
        </div>
      }

      @if (!topDisabled()) {
        <button
          type="button"
          (click)="showAddOtherModal.set(true)"
          class="text-xs font-semibold text-accent-500 hover:text-accent-600 transition-colors cursor-pointer text-left mt-1"
        >
          + Add other
        </button>
      }
    </div>
  </section>

  <!-- Classification & Narrative -->
  <section class="mb-8">
    <p class="text-sm font-bold text-navy-900 mb-5">Classification &amp; Narrative</p>
    <div class="flex flex-col gap-4">
      <div>
        <label class="text-xs font-semibold text-navy-600 tracking-wider mb-2 block"
          >Incident Categories</label
        >
        <app-multiselect-dropdown
          [options]="situationCategoryOptions()"
          [selected]="selectedCategoryIds()"
          (selectedChange)="selectedCategoryIds.set($event)"
          placeholder="Select categories…"
          [error]="showErrors()"
          [class.pointer-events-none]="topDisabled()"
          [class.opacity-60]="topDisabled()"
        />
        @if (showErrors() && selectedCategoryIds().length === 0) {
          <p class="text-2xs text-red-500 mt-1">Select at least one category.</p>
        }
      </div>
      <div>
        <label class="text-xs font-semibold text-navy-600 tracking-wider mb-2 block"
          >Incident Narrative</label
        >
        <textarea
          rows="4"
          placeholder="Describe the incident…"
          [value]="narrative()"
          (input)="narrative.set($any($event.target).value)"
          [disabled]="topDisabled()"
          class="w-full resize-none"
        ></textarea>
      </div>
    </div>
  </section>

  <!-- Actions & Immediate Response -->
  <section class="mb-8">
    <p class="text-sm font-bold text-navy-900 mb-5">Actions &amp; Immediate Response</p>
    <div class="flex flex-col gap-4">
      <div>
        <label class="text-xs font-semibold text-navy-600 tracking-wider mb-2 block"
          >Incident Responses</label
        >
        <app-multiselect-dropdown
          [options]="responseCategoryOptions()"
          [selected]="selectedResponseIds()"
          (selectedChange)="selectedResponseIds.set($event)"
          placeholder="Select responses…"
          [class.pointer-events-none]="topDisabled()"
          [class.opacity-60]="topDisabled()"
        />
      </div>
      <div>
        <label class="text-xs font-semibold text-navy-600 tracking-wider mb-2 block"
          >Additional Notes</label
        >
        <textarea
          rows="3"
          placeholder="Additional observations…"
          [value]="additionalNotes()"
          (input)="additionalNotes.set($any($event.target).value)"
          [disabled]="topDisabled()"
          class="w-full resize-none"
        ></textarea>
      </div>
    </div>
  </section>

  <!-- Configured Data Elements -->
  <section class="mb-8">
    <p class="text-sm font-bold text-navy-900 mb-2">Additional Data</p>

    @if (middleDataElements().length > 0) {
      <div class="rounded-2xl border border-surface-400 bg-white">
        <div
          class="grid grid-cols-12 items-center gap-x-3 px-4 sm:px-5 py-2.5 bg-slate-50 border-b border-surface-400 rounded-t-2xl"
        >
          <span class="col-span-1 text-2xs font-semibold text-slate-400">&#123;&#125;</span>
          <span class="col-span-5 text-2xs font-semibold text-slate-500 tracking-wider uppercase"
            >Data Element</span
          >
          <span class="col-span-5 text-2xs font-semibold text-slate-500 tracking-wider uppercase"
            >Value</span
          >
          <span class="col-span-1"></span>
        </div>
        @for (el of middleDataElements(); track el.order) {
          <div
            class="grid grid-cols-12 items-center gap-x-3 px-4 sm:px-5 py-3 border-b border-slate-50 last:border-0"
          >
            <span class="col-span-1 text-2xs text-slate-400">{{ el.order }}.</span>
            <span class="col-span-5 text-xs text-navy-900 min-w-0 truncate">{{ el.label }}</span>
            <div class="col-span-5">
              @if (el.dataType === 'Datetime') {
                <input
                  type="date"
                  class="w-full border-0 bg-transparent p-0 text-xs text-navy-900 focus:ring-0 focus:outline-none cursor-pointer"
                  [value]="el.value"
                  [disabled]="topDisabled()"
                  (click)="$any($event.target).showPicker?.()"
                  (change)="onMiddleElementChange(el, $any($event.target).value)"
                />
              } @else if (el.dataType === 'Note') {
                <textarea
                  rows="2"
                  placeholder="—"
                  class="w-full border-0 bg-transparent p-0 text-xs focus:ring-0 focus:outline-none resize-none"
                  [ngClass]="el.value ? 'text-navy-900' : 'text-slate-400'"
                  [disabled]="topDisabled()"
                  (input)="onMiddleElementChange(el, $any($event.target).value)"
                  >{{ el.value }}</textarea
                >
              } @else if (el.dataType === 'Integer' || el.dataType === 'Float') {
                <input
                  type="number"
                  [step]="el.dataType === 'Float' ? 'any' : '1'"
                  placeholder="—"
                  class="border-0 bg-transparent p-0 text-xs focus:ring-0 focus:outline-none"
                  [ngClass]="el.value ? 'text-navy-900' : 'text-slate-400'"
                  [value]="el.value"
                  [disabled]="topDisabled()"
                  (input)="onMiddleElementChange(el, $any($event.target).value)"
                />
              } @else if (el.dataType === 'Yes/No' || el.dataType === 'List Item') {
                <app-select
                  [borderless]="true"
                  [options]="el.options"
                  [keepOrder]="true"
                  [value]="el.value"
                  placeholder="Select"
                  (changed)="onMiddleElementChange(el, $event)"
                  [class.pointer-events-none]="topDisabled()"
                  [class.opacity-60]="topDisabled()"
                />
              } @else {
                <input
                  type="text"
                  placeholder="—"
                  class="border-0 bg-transparent p-0 text-xs focus:ring-0 focus:outline-none"
                  [ngClass]="el.value ? 'text-navy-900' : 'text-slate-400'"
                  [value]="el.value"
                  [disabled]="topDisabled()"
                  (input)="onMiddleElementChange(el, $any($event.target).value)"
                />
              }
            </div>
            <div class="col-span-1 flex justify-end">
              @if (el.completed) {
                <img src="/icons/check-circle-green-icon.svg" class="w-5 h-5" alt="" />
              } @else if (
                (showErrors() || showDataElementErrors()) && el.required && !el.completed
              ) {
                <div
                  class="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center shrink-0"
                >
                  <img src="/icons/close-icon.svg" class="w-2.5 h-2.5 brightness-0 invert" alt="" />
                </div>
              } @else {
                <div class="w-5 h-5 rounded-full border-2 border-slate-200"></div>
              }
            </div>
          </div>
        }
      </div>
    } @else {
      <p class="text-xs text-slate-400">
        {{
          program()
            ? 'This program has no additional data.'
            : 'Select a program to load additional data.'
        }}
      </p>
    }
  </section>

  <div class="border-t border-surface-400 mb-8"></div>

  <!-- Reports completed -->
  <section class="mb-8">
    <p class="text-xs font-semibold text-navy-600 tracking-wider mb-3">Reports completed</p>
    <div class="flex flex-col gap-2">
      @for (row of filteredReports(); track $index) {
        <div
          class="flex items-center gap-3 bg-white rounded-2xl border border-surface-400 px-4 py-3 group"
          [class.cursor-pointer]="!middleLocked()"
          [class.hover:border-accent-300]="!middleLocked()"
          [class.hover:shadow-sm]="!middleLocked()"
          (click)="!middleLocked() && onEditReport($index)"
        >
          <div class="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
            <img src="/icons/flag-icon.svg" class="w-4 h-4" alt="" />
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-xs font-semibold text-navy-900">{{ row.recipient }}</p>
            <p class="text-2xs text-slate-400">{{ row.reference }}</p>
          </div>
          <span class="text-xs text-slate-400 shrink-0">{{ row.date }}</span>
          @if (!middleLocked()) {
            <img
              src="/icons/edit-icon.svg"
              class="w-4 h-4 shrink-0 opacity-0 group-hover:opacity-60 transition-opacity"
              alt="Edit"
            />
          } @else {
            <img src="/icons/check-circle-green-icon.svg" class="w-5 h-5 shrink-0" alt="" />
          }
        </div>
      }
      @if (!filteredReports().length) {
        <p class="text-xs text-slate-400 py-1">No reports completed yet.</p>
      }
    </div>
    @if (!middleLocked()) {
      <button
        type="button"
        (click)="onOpenAddReportModal()"
        class="mt-4 text-xs font-semibold text-primary-600 hover:text-accent-700 transition-colors cursor-pointer underline"
      >
        + Add Report
      </button>
    }
  </section>

  <!-- Document attachments -->
  <section class="mb-8">
    <p class="text-xs font-semibold text-navy-600 tracking-wider mb-3">Documents</p>
    @if (incident()) {
      <app-file-uploader
        [incidentId]="incident()!.id"
        [existingDocuments]="documents()"
        [readonly]="currentStage() === 'closed'"
        (uploaded)="saved.emit()"
        (deleted)="saved.emit()"
      />
    } @else {
      <p class="text-xs text-slate-400 py-1">
        Documents can be attached once the incident is saved.
      </p>
    }
  </section>

  @if (!topDisabled() && (currentStage() === 'A' || !incident())) {
    <!-- Stage A flag — hidden once submitted -->
    <section class="mb-8">
      <p class="text-sm font-bold text-navy-900 mb-5">Submission</p>
      <label class="flex items-start gap-3 select-none group cursor-pointer">
        <div
          class="mt-0.5 w-5 h-5 rounded flex items-center justify-center shrink-0 border-2 transition-colors"
          [ngClass]="
            stageAFlag()
              ? 'bg-accent-500 border-accent-500'
              : 'bg-white border-slate-300 group-hover:border-accent-400'
          "
          (click)="stageAFlag.set(!stageAFlag())"
        >
          @if (stageAFlag()) {
            <img src="/icons/check-white-icon.svg" class="w-3 h-3" alt="" />
          }
        </div>
        <div>
          <p class="text-xs font-semibold text-navy-900">Submitted for Supervisor Review</p>
          <p class="text-2xs text-slate-400 mt-0.5">
            Incident has been submitted for supervisor review. This cannot be undone.
          </p>
        </div>
      </label>
    </section>
  }

  @if (showMiddle()) {
    <div class="border-t-2 border-surface-400 mb-8 mt-2"></div>
    <p class="text-sm font-bold text-navy-900 mb-5">Stage B: Supervisor Review</p>

    <!-- Supervisor Note (optional, JCCA-169) -->
    <section class="mb-8">
      <label class="text-xs font-semibold text-navy-600 tracking-wider mb-2 block"
        >Supervisor Note <span class="font-normal text-slate-400">(optional)</span></label
      >
      <textarea
        rows="4"
        placeholder="Add a note for this review…"
        [value]="supervisorNote()"
        (input)="supervisorNote.set($any($event.target).value)"
        [disabled]="!canEditMiddle() || stageBLocked()"
        class="w-full resize-none"
      ></textarea>
    </section>

    <!-- Ready to submit (Stage B only) -->
    @if (currentStage() === 'B') {
      <section class="mb-8">
        <p class="text-sm font-bold text-navy-900 mb-0.5">Ready to submit for Management Review?</p>
        <p class="text-xs text-slate-500 mb-4">
          Confirm that all reports are completed and all documents are attached.
        </p>

        <label
          class="flex items-center gap-3 w-fit select-none"
          [class.cursor-pointer]="canEditMiddle() && !middleLocked()"
          [class.cursor-default]="!canEditMiddle() || middleLocked()"
          [class.opacity-50]="!canEditMiddle() || middleLocked()"
        >
          <input
            type="checkbox"
            [checked]="stageBFlag()"
            [disabled]="!canEditMiddle() || middleLocked()"
            (change)="onToggleStageBFlag($any($event.target).checked)"
            class="w-4 h-4 rounded cursor-pointer accent-accent-500"
          />
          <span class="text-sm font-medium text-navy-900">Ready to submit for review</span>
        </label>
        @if (stageBFlag() && !stageAValid()) {
          <p class="text-xs text-red-500 mt-1">
            All clients and others must have an involvement type and completed data elements before
            marking as ready.
          </p>
        }
      </section>
    }

    <!-- Stage B save action -->
    @if (currentStage() === 'B') {
      <div class="flex flex-wrap justify-end gap-3 pb-2">
        @if (canReturnToPreviousStage()) {
          <ng-container [ngTemplateOutlet]="returnToPreviousStage" />
        }
        <button
          type="button"
          (click)="onSave()"
          [disabled]="saving() || (stageBFlag() && !stageAValid())"
          class="px-6 py-2.5 text-sm font-semibold text-white bg-accent-500 hover:opacity-90 rounded-xl transition-opacity cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ saving() ? 'Saving…' : stageBFlag() ? 'Send to Follow-Up' : 'Save' }}
        </button>
      </div>
    }
  }

  <!-- STAGE C — Leadership Review (Program Leadership+) -->
  @if (showFollowUp()) {
    <!-- Incident Resolution -->
    <section class="mb-8">
      <div class="border-t-2 border-surface-400 mb-8 mt-2"></div>
      <p class="text-sm font-bold text-navy-900 mb-5">Stage C: Management Review</p>
      <div>
        <label class="text-xs font-semibold text-navy-600 tracking-wider mb-2 block"
          >Incident Follow-Up</label
        >
        <textarea
          rows="5"
          placeholder="Describe the incident…"
          [value]="incidentFollowup()"
          (input)="incidentFollowup.set($any($event.target).value)"
          [disabled]="!canEditFollowUp() || stageCLocked()"
          class="w-full resize-none"
        ></textarea>
      </div>
    </section>

    <section class="mb-8">
      <div class="flex flex-col gap-4">
        <label
          class="flex items-center gap-3 w-fit select-none"
          [class.cursor-pointer]="canEditFollowUp() && !stageCLocked()"
          [class.cursor-default]="!canEditFollowUp() || stageCLocked()"
          [class.opacity-50]="!canEditFollowUp() || stageCLocked()"
        >
          <input
            type="checkbox"
            [checked]="stageCFlag()"
            [disabled]="!canEditFollowUp() || stageCLocked()"
            (change)="onStageCFlagChange($any($event.target).checked)"
            class="w-4 h-4 rounded cursor-pointer accent-accent-500"
          />
          <span class="text-sm font-medium text-navy-900">Management Review Completed</span>
        </label>

        <label class="flex items-center gap-3 w-fit select-none cursor-default">
          <input
            type="checkbox"
            [checked]="stageCNotApplicable()"
            disabled
            class="w-4 h-4 rounded cursor-not-allowed"
          />
          <span class="text-sm font-medium text-slate-400">Management Review Not Required</span>
        </label>
      </div>
    </section>

    <!-- Stage C save action -->
    @if (currentStage() === 'C') {
      @if (stageCFlag() && !stageAValid()) {
        <p class="text-xs text-red-500 mb-3 text-right">
          All clients and others must have an involvement type and completed data elements before
          sending to compliance.
        </p>
      }
      <div class="flex flex-wrap justify-end gap-3 pb-2">
        @if (canReturnToPreviousStage()) {
          <ng-container [ngTemplateOutlet]="returnToPreviousStage" />
        }
        <button
          type="button"
          (click)="onSave()"
          [disabled]="saving() || (stageCFlag() && !stageAValid())"
          class="px-6 py-2.5 text-sm font-semibold text-white bg-accent-500 hover:opacity-90 rounded-xl transition-opacity cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ saving() ? 'Saving…' : stageCFlag() ? 'Send to Compliance' : 'Save' }}
        </button>
      </div>
    }
  }

  <!-- LOWER SECTION -->

  @if (showLower()) {
    <section class="mb-8">
      <div class="border-t-2 border-surface-400 mb-8 mt-2"></div>
      <p class="text-sm font-bold text-navy-900 mb-5">
        Stage D: Compliance &amp; Program Quality Review
      </p>
      <div class="flex flex-col gap-8">
        <!-- Program Quality -->
        <div class="flex flex-col gap-3">
          <label class="text-xs font-semibold text-navy-600 tracking-wider"
            >Program Quality Review Note (Optional)</label
          >
          <textarea
            rows="5"
            placeholder="Enter optional program quality observations or performance notes…"
            [value]="pqNote()"
            (input)="pqNote.set($any($event.target).value)"
            [disabled]="lowerDisabled()"
            class="w-full resize-none"
          ></textarea>
          <label
            class="flex items-center gap-3 w-fit select-none"
            [class.cursor-pointer]="!lowerDisabled()"
            [class.cursor-default]="lowerDisabled()"
            [class.opacity-50]="lowerDisabled()"
          >
            <input
              type="checkbox"
              [checked]="stageDPQ()"
              [disabled]="lowerDisabled()"
              (change)="stageDPQ.set($any($event.target).checked)"
              class="w-4 h-4 rounded cursor-pointer accent-accent-500"
            />
            <span class="text-sm font-medium text-navy-900">Reviewed by PQ</span>
          </label>
        </div>

        <!-- Compliance -->
        <div class="flex flex-col gap-3">
          <label class="text-xs font-semibold text-navy-600 tracking-wider"
            >Compliance Review Note (Optional)</label
          >
          <textarea
            rows="5"
            placeholder="Enter optional compliance findings or regulatory notes…"
            [value]="complianceNote()"
            (input)="complianceNote.set($any($event.target).value)"
            [disabled]="lowerDisabled()"
            class="w-full resize-none"
          ></textarea>
          <label
            class="flex items-center gap-3 w-fit select-none"
            [class.cursor-pointer]="!lowerDisabled()"
            [class.cursor-default]="lowerDisabled()"
            [class.opacity-50]="lowerDisabled()"
          >
            <input
              type="checkbox"
              [checked]="stageDCompliance()"
              [disabled]="lowerDisabled()"
              (change)="stageDCompliance.set($any($event.target).checked)"
              class="w-4 h-4 rounded cursor-pointer accent-accent-500"
            />
            <span class="text-sm font-medium text-navy-900">Reviewed by Compliance</span>
          </label>
        </div>
      </div>
    </section>
  }

  <!-- Save button (top section only — middle section has its own Send to Follow-Up) -->
  @if (!topDisabled() && !showMiddle()) {
    <div class="flex justify-end pt-2 pb-6">
      <button
        type="button"
        (click)="onSave()"
        [disabled]="saving() || !stageAHasAnyValue() || (stageAFlag() && !stageAValid())"
        class="px-6 py-2.5 text-sm font-semibold text-white bg-accent-500 hover:opacity-90 rounded-xl transition-opacity cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {{ saving() ? 'Saving…' : stageAFlag() ? 'Submit for Review' : 'Save' }}
      </button>
    </div>
  }

  <!-- Lower section save button -->
  @if (showLower() && currentStage() !== 'closed' && !lowerLocked()) {
    <div class="flex flex-wrap justify-end gap-3 pt-2 pb-6">
      @if (currentStage() === 'D' && canReturnToPreviousStage()) {
        <ng-container [ngTemplateOutlet]="returnToPreviousStage" />
      }
      <button
        type="button"
        (click)="onSave()"
        [disabled]="saving()"
        class="px-6 py-2.5 text-sm font-semibold text-white bg-accent-500 hover:opacity-90 rounded-xl transition-opacity cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {{ saving() ? 'Saving…' : 'Save' }}
      </button>
    </div>
  }

  <!-- Done: return to Stage D, at the bottom of section D -->
  @if (showLower() && currentStage() === 'closed' && canReturnToPreviousStage()) {
    <div class="flex flex-wrap gap-3 pt-2 pb-6">
      <ng-container [ngTemplateOutlet]="returnToPreviousStage" />
    </div>
  }
</div>

<!-- Return to Previous Stage (JCCA-129). Red and pushed to the left, away from Save. -->
<ng-template #returnToPreviousStage>
  <button
    type="button"
    (click)="showReturnModal.set(true)"
    [disabled]="returning() || saving()"
    class="mr-auto px-6 py-2.5 text-sm font-semibold text-white bg-red-500 hover:opacity-90 rounded-xl transition-opacity cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
  >
    Return to Previous Stage
  </button>
</ng-template>

@if (showReturnModal()) {
  <app-confirm-modal
    title="Return to Previous Stage?"
    [message]="returnModalMessage()"
    [confirmLabel]="returning() ? 'Returning…' : 'Confirm'"
    (closed)="!returning() && showReturnModal.set(false)"
    (confirmed)="onReturnToPreviousStage()"
  />
}

<!-- Process Concluded modal -->
@if (showClosedModal()) {
  <div class="fixed inset-0 bg-black/40 flex items-center justify-center z-200 p-4">
    <div class="bg-white rounded-3xl w-full max-w-sm shadow-xl p-6 text-center">
      <div
        class="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4"
      >
        <img src="/icons/check-circle-green-icon.svg" class="w-7 h-7" alt="" />
      </div>
      <p class="text-sm font-bold text-navy-900 mb-1">Process Concluded</p>
      <p class="text-xs text-slate-500 mb-6">
        The incident has completed all review stages and is now closed.
      </p>
      <button
        type="button"
        (click)="router.navigate(['/incidents'])"
        class="w-full px-4 py-2.5 text-sm font-semibold text-white bg-accent-500 hover:opacity-90 rounded-xl transition-opacity cursor-pointer"
      >
        Go to Incidents
      </button>
    </div>
  </div>
}

<!-- Add Report modal — rendered at root level to avoid overflow/stacking context issues -->
@if (showAddReportModal()) {
  <div
    class="fixed inset-0 bg-black/40 flex items-center justify-center z-200 p-4"
    (click)="showAddReportModal.set(false); editingReportIndex.set(null)"
  >
    <div
      class="bg-white rounded-3xl w-full max-w-sm shadow-xl p-6 relative"
      (click)="$event.stopPropagation()"
    >
      <button
        type="button"
        (click)="showAddReportModal.set(false); editingReportIndex.set(null)"
        class="absolute top-4 right-4 w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors cursor-pointer text-sm"
      >
        ✕
      </button>
      <p class="text-sm font-bold text-navy-900 mb-0.5">
        {{ editingReportIndex() !== null ? 'Edit Report' : 'Add Report' }}
      </p>
      <p class="text-xs text-slate-500 mb-5">New report to this incident</p>
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-semibold text-navy-600 tracking-wider">Recipient</label>
          <app-select
            [options]="recipientPickerOptions()"
            [value]="reportDraft().recipientId"
            placeholder="Select recipient…"
            (changed)="reportDraft.update((d) => ({ ...d, recipientId: $event }))"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-semibold text-navy-600 tracking-wider">Reference Number</label>
          <input
            type="text"
            placeholder="Type name…"
            class="w-full"
            [value]="reportDraft().referenceNumber"
            (input)="
              reportDraft.update((d) => ({ ...d, referenceNumber: $any($event.target).value }))
            "
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-semibold text-navy-600 tracking-wider">Date</label>
          <app-date-picker
            [value]="reportDraft().date"
            [hideTime]="true"
            (valueChange)="reportDraft.update((d) => ({ ...d, date: $event }))"
            class="w-full"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-semibold text-navy-600 tracking-wider">Note</label>
          <textarea
            rows="3"
            placeholder="Add a note…"
            class="w-full resize-none"
            [value]="reportDraft().note"
            (input)="reportDraft.update((d) => ({ ...d, note: $any($event.target).value }))"
          ></textarea>
        </div>
      </div>
      <div class="flex items-center justify-end gap-3 mt-6">
        <button
          type="button"
          (click)="showAddReportModal.set(false); editingReportIndex.set(null)"
          class="px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="button"
          (click)="onAddReport()"
          class="px-5 py-2 text-sm font-semibold text-white bg-accent-500 hover:opacity-90 rounded-xl transition-opacity cursor-pointer"
        >
          {{ editingReportIndex() !== null ? 'Save Changes' : 'Add Report' }}
        </button>
      </div>
    </div>
  </div>
}
